import React, { ReactNode, useState, useEffect, useRef } from "react";
import {
  Drawer,
  DrawerContent,
  DrawerItem,
  DrawerItemProps,
  DrawerSelectEvent,
} from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import openPanel from "../../assets/images/left-panel-close.svg";
import closePanel from "../../assets/images/left-panel-open.svg";
import ChatAI from "./Chat";
import Conversation from "./Conversation";
import { MessageData } from "../../types/chat-bot-hub/MessageData";
import useAuth from "../../hooks/useAuth";
import { API_BASE_URL } from "../../constants";
import * as signalR from '@microsoft/signalr';
import { MessageResult } from "../../types/chat-bot-hub/MessageResult";
import { MessageRequest } from "../../types/chat-bot-hub/MessageRequest";
import { MessageResponse } from "../../types/chat-bot-hub/MessageResponse";
import chatBotService from "../../services/chatBot.service"
import Swal from "sweetalert2";
import { UserChatDetails } from "../../types/chat-bot-hub/UserChatDetails";
import { Loader } from "@progress/kendo-react-indicators";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu"
import { MoreVertical, Trash } from "lucide-react";
import { quickQueryCards } from "../../types/chat-bot-hub/QuickQueryCards";
import { useTeamsAuth } from "../../hooks/useTeamsAuth";
import { recordingNetworkUsers } from "../../types/chat-bot-hub/recordingNetworkUsers";
import "./ChatManagement.scss"
import useNavigationBlocker from "../../hooks/useNavigationBlocker";

interface INavItem {
  text: string;
  icon: string;
  selected: boolean;
  content?: ReactNode;
  className?: string;
  sessionid?: string;
  chatid? : string;
  tabid?: string;
  title?:string;
  responseComplete?:boolean;
  onClick?: () => void;
}
 
function ChatManagement() {
  const chatBotConnection = useRef<signalR.HubConnection>();
  const [expanded, setExpanded] = useState(true);
  const [navItems, setNavItems] = useState<INavItem[]>([]);
  const [selectedId, setSelectedId] = useState(0);
  const [messages, setMessages] = useState<MessageData[]>([]);
  const [historicMessages, setHistoricMessages] = useState<UserChatDetails[]>([]);
  const [chatHistory, setChatHistory] = useState<UserChatDetails | null>();
  const [loading, setLoading] = useState<boolean>(false);
  const [previousSessionId, setPreviousSessionId] = useState<string>("");
  const [previousChatId, setPreviousChatId] = useState<string>("");
  const [quickQueryCards,setQuickQueryCards] = useState<quickQueryCards[]>([]);
  const [recordingNetworkUsers,setRecordingNetworkUsers] = useState<recordingNetworkUsers|null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const auth = useAuth();
  const teamsAuth = useTeamsAuth();
  let token = auth?.accessToken ?? teamsAuth?.voiceAIToken;
  const connectionUrl = `${API_BASE_URL}/chatbot`;
 
  useEffect(() => {
    const fetchChats = async () => {
      const chats = await GetUserChats();
      if (chats) {
        setNavItems(getTabItems(chats));
      }
    };
    fetchChats();
  }, []); 

  useEffect(() => {
  const loadCards = async () => {
    const cards = await fetchqueryCards();
    setQuickQueryCards(cards);
  };
  loadCards();
  const fetchRecordedUsers = async () => {
    const users = await fetchTotalRecordedUsers();
    setRecordingNetworkUsers(users);
  };
  fetchRecordedUsers();
  }, []);

  useEffect(()=>
  {
    setHasUnsavedChanges(navItems.some(
  n => n.responseComplete === false));
  
  },[navItems]);
  // useEffect for handling browser back/forward buttons
useEffect(() => {
  const handlePopState = (e: PopStateEvent) => {
    if (hasUnsavedChanges) {
      const confirmLeave = window.confirm(
        'If you navigate away from this page while awaiting a response, the ongoing request will not be completed. Are you sure you want to leave?'
      );
      if (!confirmLeave) {
        window.history.pushState(null, '', window.location.href);
      }
    }
  };

  // enable back button detection
  if (hasUnsavedChanges) {
    window.history.pushState(null, '', window.location.href);
  }

  window.addEventListener('popstate', handlePopState);

  return () => {
    window.removeEventListener('popstate', handlePopState);
  };
}, [hasUnsavedChanges]);
// useEffect for handling tab close
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'If you navigate away from this page while awaiting a response, the ongoing request will not be completed. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);
  //handling navigation within app
useNavigationBlocker(
    hasUnsavedChanges,
    "If you navigate away from this page while awaiting a response, the ongoing request will not be completed. Are you sure you want to leave?"
  );

  useEffect(() => {
      setNavItems(getTabItems(historicMessages));
  }, [selectedId, historicMessages]);

useEffect(() => {
  if (!chatHistory) return;

  sessionStorage.setItem(
    `chatHistory_${chatHistory.sessionId}`,
    JSON.stringify(chatHistory)
  );
}, [chatHistory]);


  useEffect(() => {
    if (token === undefined) {
      alert("you're logged out.");
    }
    let _connection = new signalR.HubConnectionBuilder()
      .withUrl(connectionUrl, { 
        accessTokenFactory: () => token ?? "",
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets
      })
      .withAutomaticReconnect().build();
    _connection.on("ReceiveMessage", handleReceivedMessage);
    _connection.start()
      .then(() => {
        console.log(_connection);
        chatBotConnection.current = _connection
      })
      .catch(err => {
        console.error('Error while connecting to chat bot: ', err)
      });

    return () => {
      console.log("stopping the connection");
      sendTextToChatBot("",true,"");
      _connection?.stop();
      Object.keys(sessionStorage)
      .filter((key) => key.startsWith("chatHistory_"))
      .forEach((key) => sessionStorage.removeItem(key));
    };
  }, [token])

    const updateSessionCache = (sessionId: string, updater: (cached: UserChatDetails) => UserChatDetails) => {
    const storageKey = `chatHistory_${sessionId}`;
    const cachedData = sessionStorage.getItem(storageKey);
    
    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData) as UserChatDetails;
        const updated = updater(parsed);
        sessionStorage.setItem(storageKey, JSON.stringify(updated));
      } catch (err) {
        console.error('Error updating session cache:', err);
      }
    }
  };
  const handleExpandChange = () => {
    setExpanded((prev) => !prev);
  };

  const handleSelectionChange = (event: DrawerSelectEvent) => {
     const currentItem = navItems[selectedId];
     setMessages([]);

    if (currentItem?.sessionid) {
      setPreviousSessionId(currentItem.sessionid);
    }
     if (currentItem?.chatid) {
      setPreviousChatId(currentItem.chatid);
    }
    setSelectedId(event.itemIndex);
    setExpanded(true);
    const selectedItem = navItems[event.itemIndex];
    if (selectedItem?.onClick) {
      selectedItem.onClick();
    }
  };  

  const handleReceivedMessage = (user: string, message: string) => {
  let _messageResult: MessageResult = JSON.parse(message);
   const targetSessionId = _messageResult.SessionId;
  if(_messageResult.Result)
  {
  const updateMessage = (messages: MessageData[]) => {
    return messages.map((m) => {
      if (m.requestId === _messageResult.RequestId && m.sessionId === _messageResult.SessionId) {
        return {
          ...m,
          answers: [
            ...m.answers,
            {
              text: _messageResult.Result,
              timeStamp: new Date()
            }
          ],
          isCompleted: _messageResult.IsCompleted,
          sessionId: targetSessionId
        };
      }
      return m;
    });
  };

  setMessages((prev) => updateMessage(prev));

  setChatHistory((prev) => {
     if (!prev || prev.sessionId !== targetSessionId) {
          return prev;
        }

    return {
      ...prev,
      historicMessages: updateMessage(prev.historicMessages),
      responseComplete: _messageResult.IsCompleted,
      lastActivityDateTime: new Date()
    };
  });
   updateSessionCache(targetSessionId, (cached) => ({
        ...cached,
        historicMessages: updateMessage(cached.historicMessages),
        responseComplete: _messageResult.IsCompleted,
        lastActivityDateTime: new Date()
      }));
}

if(_messageResult.IsCompleted)
  {
      setHistoricMessages(prev =>
        prev.map(m =>
          m && m.sessionId === _messageResult.SessionId
            ? { ...m, responseComplete: true }   // update this one
            : m                                   // keep all others same
        )
      );

  }
 
};

  const sendTextToChatBot = (sessionId: string = "", isCancellationRequest: boolean = false, msg: string = "") => {
    const isChatSwitched = (previousSessionId !== sessionId && previousSessionId !== "");
    const isNewChat = !sessionId;
    var chatId = isNewChat ? "": historicMessages.find(m => m != null && m.sessionId == sessionId)?.chatIdentifier?? "";
    if(sessionId)
    {
          setHistoricMessages(prev =>
        prev.map(m =>
          m && m.sessionId === sessionId
            ? { ...m, responseComplete: false }   
            : m                                  
        )
      );
    }
    
    if(isChatSwitched || isNewChat)
    {
      setMessages([]);
    }
    var oldChatId = isChatSwitched||  isNewChat ? previousChatId : "";
     if (chatBotConnection) {
      let _messageRequest: MessageRequest = {
        ChatId: chatId,
        OldChatId:oldChatId,
        SessionId: sessionId,
        Message: isCancellationRequest ? "" : msg,
        IsCancellationRequest: isCancellationRequest,
        IsChatInitiated:previousSessionId === sessionId && !isNewChat ? false : true,
        RequestId: ""
      }
      chatBotConnection.current?.invoke("SendMessage", chatBotConnection.current?.connectionId, JSON.stringify(_messageRequest))
        .then((response: string) => {
          let _messageResponse: MessageResponse = JSON.parse(response);
          if (_messageResponse.IsSuccess) {
            let _message: MessageData = {
              requestId: _messageResponse.RequestId,
              chatId: _messageResponse.ChatId,
              sessionId: _messageResponse.SessionId,
              questions: { text: msg, timeStamp: new Date() },
              answers: [],
              isCancelled: _messageResponse.IsCancelled,
              isCompleted: false,
              isErrored: !_messageResponse.IsSuccess,
              error: _messageResponse.Error
            };
            if(!_messageResponse.IsCancelled)
            {
              setMessages((prev) => ([...prev, _message]));
              let _chatHistory: UserChatDetails = {
                sessionId : _messageResponse.SessionId,
                title : historicMessages.find(m => m != null && m.sessionId == _messageResponse.SessionId)?.title ?? _messageResponse.Title,
                createdDateTime: new Date(),
                lastActivityDateTime: new Date(),
                historicMessages :isNewChat?[_message]: [...messages, _message],
                isActive : true,
                chatIdentifier : _messageResponse.ChatId,
                responseComplete: false
            };
            if (isNewChat && _messageResponse.SessionId) {
            setHistoricMessages((prev) => {
                const updated = [_chatHistory, ...prev];
                setNavItems(getTabItems(updated));
                return updated;
              }); 
            setChatHistory(_chatHistory);
            setPreviousSessionId(_messageResponse.SessionId);
            setPreviousChatId(_messageResponse.ChatId);
             sessionStorage.setItem(
                `chatHistory_${_messageResponse.SessionId}`,
                JSON.stringify(_chatHistory)
              );
            setTimeout(() => setSelectedId(2), 0);
            }
            else
            {
                 setChatHistory((prev) => {
                    if (!prev || prev.sessionId !== sessionId) {
                      return prev;
                    }
                    const updated = {
                        ...prev,
                        historicMessages: [...prev.historicMessages, _message],
                        responseComplete: false
                        };
                    return updated;
                  });

                updateSessionCache(_messageResponse.SessionId, (cached) => ({
                      ...cached,
                      historicMessages: [...cached.historicMessages,_message],
                      responseComplete:false,
                      lastActivityDateTime: new Date()
                    }));
            }
            }
            else
            {
              console.log("queries cancelled");
            }
          }
          else {
            Swal.fire({
              icon: "error",
              title: `Error while sending request!`,
              text: `${_messageResponse.Error}`
            });
          }
        })
        .catch(err => console.error(err));
    }
  }

  const GetUserChats = async (): Promise<UserChatDetails[]> => {
    try {
      setLoading(true);
      const resp = await chatBotService.getChatsById();
      setHistoricMessages(resp);
      return resp;
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
    return [];
  }
    async function fetchqueryCards(): Promise<quickQueryCards[]> {
      try {
        setLoading(true);
        const resp = await chatBotService.getQuickQueryCards();
        return resp;
      } catch (err) {
        console.error(err);
        return [];
      } finally {
        setLoading(false);
      }
    }

  async function fetchTotalRecordedUsers(): Promise<recordingNetworkUsers> {
    const defaultRecordingNetworkUsers: recordingNetworkUsers = {
    totalCount: 0,
    licensedUsers: 0,
    listenPermissionGranted: true
    };

      try {
        setLoading(true);
        const resp = await chatBotService.fetchTotalRecordedUsers();
        return resp;
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
      return defaultRecordingNetworkUsers;
    }
 
const GetChatHistory = async (sessionId: number): Promise<UserChatDetails | null> => {
    try {
      const storageKey = `chatHistory_${sessionId}`;
      const cachedData = sessionStorage.getItem(storageKey);

        if (cachedData && JSON.parse(cachedData).historicMessages.length > 0) {
         return JSON.parse(cachedData) as UserChatDetails;
       }
      
      setLoading(true);
      const resp = await chatBotService.getChatHistoryBySessionId(sessionId);
        setHistoricMessages(prev =>
        prev.map(m =>
          m && m.sessionId === sessionId.toString()
            ? { ...m, responseComplete: resp.responseComplete }   
            : m                                  
        )
      );
      if (resp) {
        sessionStorage.setItem(storageKey, JSON.stringify(resp));
      }
      
      return resp;
    } catch (err) {
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
}
  
  const ConvertToNum = (sessionIdStr: string): number => {
    try {
      return Number(sessionIdStr);
    } catch {
      return 0;
    }
  };

  const handleDeleteChat = async (sessionId: string) => {
    try {
         await chatBotService.deleteChat(ConvertToNum(sessionId));
        
        setHistoricMessages((prev) => prev.filter(msg => msg.sessionId !== sessionId));
        
        const deletedIndex = navItems.findIndex(item => item.sessionid === sessionId);
        if (deletedIndex === selectedId) {
          setSelectedId(0);
          setChatHistory(null);
          setPreviousSessionId("");
          setPreviousChatId("");
        }
        const storageKey = `chatHistory_${sessionId}`;
       sessionStorage.removeItem(storageKey);
      
    } catch (error) {
      console.error('Error deleting chat:', error);
    }
  };

  const CustomItem = (props: DrawerItemProps & { className?: string; sessionid?: string;responseComplete?:boolean , title?: string}) => {

    const handleClick = async (e: React.MouseEvent,item:DrawerItemProps) => {
      const itemIndex = navItems.findIndex(item => item.text === props.text);
      const findTab = item.tabid === "recent";
      setMessages([]);
      if(findTab){
        setExpanded(true);
        return;
      }
      if (itemIndex !== -1) {
         const currentItem = navItems[selectedId];
        if (currentItem?.sessionid) {
          setPreviousSessionId(currentItem.sessionid);
        }
        if (currentItem?.chatid) {
          setPreviousChatId(currentItem.chatid);
        }
        setSelectedId(itemIndex);
        setExpanded(true);
        const selectedItem = navItems[itemIndex];
        if (selectedItem?.sessionid) {
          setChatHistory(null);
          const activeSession = selectedItem.sessionid;

          const history = await GetChatHistory(ConvertToNum(selectedItem.sessionid));
          if (activeSession === selectedItem.sessionid) {
              setChatHistory(history);
            }
        } else if (selectedItem?.tabid === "new-chat") {
          setChatHistory(null);
        }
      }
    };

    const handleDeleteClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (props.sessionid) {
        handleDeleteChat(props.sessionid);
      }
    };

    const isDynamicItem = !!props.sessionid;

    return (
      <div className="relative group" title={!expanded? props.title ?? props.text: ""}>
        <DrawerItem
          {...props}
          className={props.className}
          onClick={(e:React.MouseEvent)=>handleClick(e,props)}
          style={{cursor:`${props.tabid==="recent" && expanded ? "default" : ""}`}}
        >
          <span
            className={`k-icon ${props.icon}`}
          />
          
          {isDynamicItem && expanded && !props.responseComplete ? <span className="k-item-text"><b>{props.text}</b></span>
          :<span className="k-item-text">{props.text}</span>}
        {isDynamicItem && expanded && (
          <DropdownMenu.Root>
          {!props.responseComplete && <span className="chat-circle-spinner">
                    <svg viewBox="0 0 50 50">
                      <circle
                        cx="25"
                        cy="25"
                        r="20"
                        fill="none"
                        strokeWidth="4"
                      />
                    </svg>
                  </span>}
            <DropdownMenu.Trigger asChild>
              <button
                className="more-btn"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="23" />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                className="group-btn"
                align="end"
                sideOffset={0}
              >
                <DropdownMenu.Item
                  className="single-btn"
                  onClick={handleDeleteClick}
                >
                  <Trash className="" />
                  Delete chat
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        )} 
        </DrawerItem>
      </div>
    );
  };

  const getTabItems = (historicMessages: UserChatDetails[] = []) => {
    const baseItems: INavItem[] = [
      {
        text: "New chat",
        icon: "bi bi-pencil-square",
        selected: true,
        tabid: "new-chat",
        title: "Start a new conversation"
      },
      {
        text: "Recent chats",
        icon: "bi bi-clock-history",
        selected: false,
        tabid: "recent",
        className: "recenttab",
        title: "View your recent conversations"
      }
    ]
    const dynamicItems: INavItem[] = historicMessages
    .map((msg, index) => ({
      text: msg.title,
      icon: "",
      selected: false,
      sessionid: msg.sessionId,
      chatid: msg.chatIdentifier,
      tabid: msg.sessionId,
      responseComplete: msg.responseComplete
    }))

    const allItems = [...baseItems, ...dynamicItems];
    return allItems.map((item, index) => ({
      ...item,
      selected: index === selectedId,
    }));
  };

  const renderTabContent = (selectedItem: INavItem | undefined) => {
    
    if (!selectedItem) return null;
    
    // New Chat tab
    if (selectedItem.tabid === "new-chat") {
       if (loading) {
      return (
        <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",   
                height: "100%",         
                width: "100%",
              }}
            ><Loader type={"infinite-spinner"}
                              themeColor={"primary"}/></div>
      );
    }
      return <ChatAI key="new-chat" messages={messages} sendTextToChatBot={sendTextToChatBot} responseCompleted = {selectedItem.responseComplete??true} quickQueryCards = {quickQueryCards} recordingNetworkDetails = {recordingNetworkUsers} />;
    }
    
    // Recent tab (empty content)
    if (selectedItem.tabid === "recent") {
      return null;
    }
    
    // Dynamic history tabs
    if (selectedItem.sessionid) {
      return (
        <Conversation
          key={selectedItem.sessionid}
          messages={chatHistory?.historicMessages || []}
          sendTextToChatBot={sendTextToChatBot} responseCompleted = {selectedItem.responseComplete??true}
        />
      );
    }
    
    return null;
  };

  return (
    <div className="mainDrawerContainer">
      <div className="mainDrawer navPushDown navPush">
        <div className="mainDrawerInner">
          <div
            className={`miniButton ${expanded ? "drawer-expanded" : "drawer-collapsed"
              }`}
          >
            <Button icon="menu" fillMode="flat" onClick={handleExpandChange}>
              <img
                src={expanded ? openPanel : closePanel}
                alt={expanded ? "Close Side Panel" : "Open Side Panel"}
                title={expanded ? "Close Side Panel" : "Open Side Panel"}
              />
            </Button>
          </div>
          <Drawer
            expanded={expanded}
            position="start"
            mode="push"
            width={230}
            mini
            items={navItems.map(item => ({
              ...item,
              sessionid: item.sessionid,
              title: item.title
            }))}
            onSelect={handleSelectionChange}
            item={CustomItem}
          >
            {loading?  <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",   
                height: "100%",         
                width: "100%",
              }}
            ><Loader type={"infinite-spinner"}
                              themeColor={"primary"}/></div>:
            <DrawerContent>
              <div className="contentPush w-100">
                {renderTabContent(navItems[selectedId])}
              </div>
            </DrawerContent>}
          </Drawer>
        </div>
      </div>
    </div>
  );
}

export default ChatManagement;


